import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

const DMCA: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            Legal
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('dmca.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
            {t('dmca.lastUpdated')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          IndeeClips respects the intellectual property rights of others and expects its users to do the same. We
          comply with the Digital Millennium Copyright Act (DMCA) and will respond to valid copyright infringement
          notices.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          1. Reporting Copyright Infringement
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If you believe that your copyrighted work has been uploaded to IndeeClips without your permission, please
          send a written DMCA notice containing the following information:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>A physical or electronic signature of the copyright owner or authorised representative</li>
          <li>Identification of the copyrighted work claimed to have been infringed</li>
          <li>The URL or specific location of the infringing content on IndeeClips</li>
          <li>Your name, address, telephone number and email address</li>
          <li>
            A statement that you have a good faith belief that the use is not authorised by the copyright owner or the
            law
          </li>
          <li>A statement that the information in the notice is accurate under penalty of perjury</li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          2. Where to Send DMCA Notices
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, lineHeight: 1.85 }}>
          Please send your DMCA notice to:
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          Email:{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          3. Counter Notification
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          If you believe your content was removed in error or misidentification, you may file a counter-notification
          with the same information listed above, along with a statement under penalty of perjury that the content was
          removed by mistake or misidentification.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          4. Repeat Infringers
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          IndeeClips maintains a policy of terminating accounts of repeat copyright infringers in appropriate
          circumstances.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          5. Response Time
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          We typically respond to valid DMCA notices within 3-5 business days. Once we receive a proper notice, we will
          remove or disable access to the allegedly infringing content.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          6. Good Faith
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          Please note that making false claims of copyright infringement may result in liability for damages, including
          costs and attorneys' fees. Only submit a DMCA notice if you genuinely believe that your copyright has been
          infringed.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          7. Contact
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.85 }}>
          For all copyright-related inquiries:
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.85 }}>
          Email:{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
          Website:{' '}
          <Typography
            component="a"
            href="https://indeeclips.com"
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            https://indeeclips.com
          </Typography>
        </Typography>
      </Container>
    </Box>
  );
};

export default DMCA;
